import { Component, computed, input, output } from '@angular/core';
import { OptionGroups, PreisIndikation, PriceIndicationCampaignTextList, TariffOptionSap, TariffSelectionCardData } from '@mwe/models';
import { InfoTextComponent } from '../../../info-text/info-text.component';
import { TariffSelectionCardUiRowComponent } from '../tariff-selection-card-ui-row.component';
import { TariffOptionDropdownComponent } from '../tariff-option-dropdown/tariff-option-dropdown.component';
import { InfoboxComponent } from '../../../infobox/infobox.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { PreserveFocusDirective } from '../../../aria/restore-focus/preserve-focus.directive';
import { InfoTextIconComponent } from '../../../info-text/info-text-icon.component';

@Component({
  selector: 'mwe-tariff-selection-card-front-ui',
  templateUrl: './tariff-selection-card-front-ui.component.html',
  imports: [
    InfoTextComponent,
    TariffSelectionCardUiRowComponent,
    TariffOptionDropdownComponent,
    InfoboxComponent,
    TranslateDirective,
    TranslatePipe,
    PreserveFocusDirective,
    InfoTextIconComponent,
  ],
})
export class TariffSelectionCardFrontUiComponent {
  selectedTariff = input.required<TariffSelectionCardData>();

  groups = input<OptionGroups[]>();
  activeCampaignTexts = input<PriceIndicationCampaignTextList[]>();
  advantages = input<string[]>();
  readonly tariffUpdated = output<TariffOptionSap>();

  isSpotTariff = computed<boolean>(() => {
    return (
      !!this.selectedTariff().priceIndication?.arbeitspreisSpotDurchschnitt &&
      !!this.selectedTariff().priceIndication?.arbeitspreisSpotMin &&
      !!this.selectedTariff().priceIndication?.arbeitspreisSpotMax
    );
  });

  selectedPriceIndication = computed<PreisIndikation>(() => this.selectedTariff()?.priceIndication);

  spotTariffPrice = computed<string>(
    () =>
      `${this.selectedTariff().priceIndication.arbeitspreisSpotMin} - ${this.selectedTariff().priceIndication.arbeitspreisSpotMax} ${this.selectedTariff().priceIndication?.arbeitspreisUnit}`,
  );

  infoText = computed(() => {
    return {
      infoTextCode: 'rechnungen.interaktiveRechnung.produktDetails.tarifKarte.spot',
      customLinkTarget: this.selectedTariff().priceIndication?.produktUrl ?? '',
    };
  });

  getTestId(type: string): string {
    return `tariff-selection-card-${type}`;
  }

  onOptionSelected(item: OptionGroups, optionId: string): void {
    // update userSelection with new selected sap option
    const newSapOption = this.selectedTariff().tariffOptionsSap.find(o => o.option === optionId);
    item.selectedTariffOptionId = optionId;

    this.tariffUpdated.emit(newSapOption);
  }

  compositionText = computed(() => {
    // Check SAP Options as Single Source of truth
    const sapOptions =
      this.selectedTariff()
        ?.tariffOptionsSap?.filter(o => o.art === 'Zusammensetzung')
        ?.map(o => o.option) ?? [];

    if (!sapOptions?.length) return null;

    // Get Options based on available SAP options
    const tariffOption = sapOptions?.map(o => {
      return this.selectedTariff().priceIndication?.tarif?.tarifOptionen?.find(to => to?.option === o);
    });

    if (!tariffOption?.length) return null;

    // Build html contents for modal to display
    return (
      tariffOption
        .map(to => {
          if (!to.iconInfoText?.length || !to.optionLabel?.length) return null;
          return `<strong>${to.optionLabel}</strong>${to.iconInfoText}`;
        })
        .filter(Boolean)
        .join('') ?? null
    );
  });
}
